import { useCallback, useMemo, useState, useEffect } from 'react';

interface SnapOptions {
  snapTargets: number[];           // Tick positions to snap to
  snapThreshold: number;           // Pixel threshold for snapping
  pixelToValue: (px: number) => number;
  valueToPixel: (value: number) => number;
  enabled?: boolean;               // Global snap enable/disable
}

interface SnapResult {
  snappedValue: number;           // The actual value to use
  isSnapping: boolean;            // Whether currently snapping
  snapTarget: number | null;      // Which target we're snapping to
  snapDistance: number;           // Distance to snap target
}

export const useDragSnap = (options: SnapOptions) => {
  const [isAltPressed, setIsAltPressed] = useState(false);

  // Global Alt key detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey) setIsAltPressed(true);
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      if (!e.altKey) setIsAltPressed(false);
    };

    // Also check for Alt key state during mouse events
    const handleMouseEvent = (e: MouseEvent) => {
      if (e.altKey !== isAltPressed) {
        setIsAltPressed(e.altKey);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousemove', handleMouseEvent);
    window.addEventListener('mousedown', handleMouseEvent);
    window.addEventListener('mouseup', handleMouseEvent);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousemove', handleMouseEvent);
      window.removeEventListener('mousedown', handleMouseEvent);
      window.removeEventListener('mouseup', handleMouseEvent);
    };
  }, [isAltPressed]);

  const calculateSnap = useCallback((rawValue: number): SnapResult => {
    // If snapping disabled or Alt pressed, return raw value
    if (!options.enabled || isAltPressed) {
      return {
        snappedValue: rawValue,
        isSnapping: false,
        snapTarget: null,
        snapDistance: 0
      };
    }

    // Find nearest snap target
    let nearestTarget: number | null = null;
    let minDistance = Infinity;

    for (const target of options.snapTargets) {
      const pixelDistance = Math.abs(
        options.valueToPixel(target) - options.valueToPixel(rawValue)
      );
      
      if (pixelDistance < minDistance && pixelDistance <= options.snapThreshold) {
        minDistance = pixelDistance;
        nearestTarget = target;
      }
    }

    if (nearestTarget !== null) {
      return {
        snappedValue: nearestTarget,
        isSnapping: true,
        snapTarget: nearestTarget,
        snapDistance: minDistance
      };
    }

    return {
      snappedValue: rawValue,
      isSnapping: false,
      snapTarget: null,
      snapDistance: 0
    };
  }, [options, isAltPressed]);

  return {
    calculateSnap,
    isAltPressed,
    snapEnabled: options.enabled && !isAltPressed
  };
}; 